import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { pluralizeToString } from '~/utils/stringUtils';

import CheckboxList from './CheckboxList';
import { Experience } from './types';
import { AttachedExperience } from './usePackageOptionsState';

// No experience search service is wired up yet, so there's nothing to select; the Next
// button on this step stays disabled until real data exists.
const EXPERIENCES: Array<Experience> = [];

export type BundledExperiencesResult = {
  assetIds: Array<string>;
  optionIndexes: Array<number>;
  sellIncrease: number;
  netIncrease: number;
  propertySellIncrease: number;
  suffix: string;
  experiences: Array<AttachedExperience>;
};

type BundledExperiencesWizardProps = {
  baseRoomAssetIds: Array<string>;
  baseRoomLabel: (assetId: string) => string;
  initialAssets: Array<string>;
  initialPropertyNet: string;
  initialPropertySell: string;
  netCurrency: string;
  initialSuffix: string;
  onApply: (result: BundledExperiencesResult) => void;
  onClose: () => void;
  open: boolean;
  optionLabels: Array<string>;
};

const EXPERIENCE_COLUMNS: Array<GridColDef> = [
  { field: 'luxPlus', headerName: 'LuxPlus', width: 90 },
  { field: 'travelTo', headerName: 'Travel To', width: 120 },
  { field: 'accountName', headerName: 'Account Name', width: 180 },
  { field: 'dealOptionName', headerName: 'Deal Option Name', width: 320 },
  { field: 'fareType', headerName: 'Fare Type', width: 110 },
  { field: 'dealCategory', headerName: 'AC_Deal_Category', width: 150 },
  { field: 'subCategory', headerName: 'Sub Category', width: 150 },
  {
    field: 'ancillarySellPriceUplift',
    headerName: 'Ancillary Sell Price Uplift',
    align: 'right',
    headerAlign: 'right',
    width: 180,
  },
  { field: 'valueCostCurrency', headerName: 'Value (Cost Currency)', align: 'right', headerAlign: 'right', width: 170 },
  { field: 'currency', headerName: 'Currency', width: 130 },
  { field: 'destination', headerName: 'Destination', width: 140 },
  { field: 'subDestination', headerName: 'Sub-Destination', width: 150 },
];

export default function BundledExperiencesWizard({
  baseRoomAssetIds,
  baseRoomLabel,
  initialAssets,
  initialPropertyNet,
  initialPropertySell,
  initialSuffix,
  netCurrency,
  onApply,
  onClose,
  open,
  optionLabels,
}: BundledExperiencesWizardProps) {
  const optionIndexes = optionLabels.map((_label, index) => index);

  const [step, setStep] = useState(1);
  const [selectedExperienceIds, setSelectedExperienceIds] = useState<Array<string>>([]);
  const [selectedAssets, setSelectedAssets] = useState<Array<string>>([]);
  const [selectedOptions, setSelectedOptions] = useState<Array<number>>(optionIndexes);
  const [suffix, setSuffix] = useState(initialSuffix);
  const [alsoPropertyNet, setAlsoPropertyNet] = useState(false);
  const [propertySell, setPropertySell] = useState('');
  const [propertyNet, setPropertyNet] = useState('');
  const [experienceSearch, setExperienceSearch] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    const carriesPropertyUplift = initialPropertySell !== '' || initialPropertyNet !== '';
    setStep(1);
    setSelectedExperienceIds([]);
    setSelectedAssets(carriesPropertyUplift ? initialAssets : []);
    setSelectedOptions(optionLabels.map((_label, index) => index));
    setSuffix(initialSuffix);
    setAlsoPropertyNet(carriesPropertyUplift);
    setPropertySell(initialPropertySell);
    setPropertyNet(initialPropertyNet);
    setExperienceSearch('');
    // Keyed on `open` only: re-running on card changes would discard in-dialog edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const searchTerm = experienceSearch.trim().toLowerCase();
  const visibleExperiences = searchTerm
    ? EXPERIENCES.filter((experience) => experience.dealOptionName.toLowerCase().includes(searchTerm))
    : EXPERIENCES;

  const selectedExperiences = EXPERIENCES.filter((experience) => selectedExperienceIds.includes(experience.id));
  const experienceSellTotal = selectedExperiences.reduce(
    (total, experience) => total + experience.ancillarySellPriceUplift,
    0,
  );
  // No FX conversion service is wired up yet; cost totals are stubbed at 0 until one exists.
  const experienceCostTotal = 0;
  const propertySellValue = alsoPropertyNet ? Number(propertySell) || 0 : 0;
  const propertyNetValue = alsoPropertyNet ? Number(propertyNet) || 0 : 0;

  function handleNext() {
    if (step === 3) {
      onApply({
        assetIds: selectedAssets,
        optionIndexes: selectedOptions,
        sellIncrease: experienceSellTotal + propertySellValue,
        netIncrease: propertyNetValue,
        propertySellIncrease: propertySellValue,
        suffix: suffix.trim(),
        experiences: selectedExperiences.map((experience) => ({
          id: experience.id,
          dealOptionName: experience.dealOptionName,
          ancillarySellPriceUplift: experience.ancillarySellPriceUplift,
          valueCostCurrency: experience.valueCostCurrency,
          currency: experience.currency,
          // No FX conversion service is wired up yet; stubbed at 0 until one exists.
          costAud: 0,
        })),
      });
      onClose();
      return;
    }
    if (step === 1) {
      setSelectedAssets([...baseRoomAssetIds]);
    }
    setStep(step + 1);
  }

  const nextDisabled =
    (step === 1 && selectedExperienceIds.length === 0) ||
    (step === 2 && (selectedAssets.length === 0 || selectedOptions.length === 0)) ||
    (step === 3 && !suffix.trim());

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
      <DialogTitle>
        {step === 1 && 'Select bundled experiences'}
        {step === 2 && 'Apply to room types'}
        {step === 3 && 'Finalize upgrade package'}
      </DialogTitle>
      <DialogContent dividers>
        {step === 1 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Please select the experiences you want to add to your deal options.
            </Typography>
            <Box mt={2}>
              <TextField
                fullWidth
                label="Search deal option name"
                onChange={(e) => setExperienceSearch(e.target.value)}
                placeholder="e.g. Transfer to the airport"
                size="small"
                value={experienceSearch}
              />
            </Box>
            <Box mt={2}>
              <DataGrid
                autoHeight
                checkboxSelection
                columns={EXPERIENCE_COLUMNS}
                disableRowSelectionOnClick
                keepNonExistentRowsSelected
                onRowSelectionModelChange={(model) => setSelectedExperienceIds(model as Array<string>)}
                rowSelectionModel={selectedExperienceIds}
                rows={visibleExperiences}
              />
            </Box>
          </>
        )}

        {step === 2 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Choose which base room types should receive the upgrade. Packages will be created as{' '}
              <Typography component="span" fontStyle="italic" variant="inherit">
                {suffix.trim() || 'Suffix'}
              </Typography>{' '}
              across the selected options.
            </Typography>
            <Box bgcolor="action.hover" borderRadius={1} mt={2} p={1.5}>
              <Typography variant="body2">
                Selected: {pluralizeToString('experience', selectedExperiences.length)} · Experience sell +$
                {Math.round(experienceSellTotal)} · Experience cost ~$
                {Math.round(experienceCostTotal)} AUD (tracked separately — package net unchanged unless you add a
                property uplift next)
              </Typography>
            </Box>

            <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} mt={2}>
              <CheckboxList
                emptyMessage="No base room types found — add rooms in Package Options first."
                items={baseRoomAssetIds}
                label={baseRoomLabel}
                onChange={setSelectedAssets}
                selected={selectedAssets}
                title="Apply to base room types"
              />
              <CheckboxList
                items={optionIndexes}
                label={(index) => optionLabels[index]}
                onChange={setSelectedOptions}
                selected={selectedOptions}
                title="Package options"
              />
            </Box>
          </>
        )}

        {step === 3 && (
          <>
            <Typography color="text.secondary" variant="body2">
              Confirm the package name and pricing. Sell increases by the property sell uplift plus experience sell;
              package net increases by the property net uplift only (experience cost is tracked separately).
            </Typography>

            <TextField
              fullWidth
              helperText="Appended as Room - Suffix"
              label="Suffix"
              onChange={(e) => setSuffix(e.target.value)}
              placeholder="e.g. Indulgence Package + Transfers"
              size="small"
              sx={{ mt: 2 }}
              value={suffix}
            />

            <Box border={1} borderColor="divider" borderRadius={1} mt={2} p={1.5}>
              <FormControlLabel
                control={<Checkbox checked={alsoPropertyNet} onChange={(e) => setAlsoPropertyNet(e.target.checked)} />}
                label="Yes — also increase property sell / net on this package"
              />
              {alsoPropertyNet && (
                <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} mt={1}>
                  <TextField
                    inputProps={{ min: 0 }}
                    label="Sell price increase (AUD)"
                    onChange={(e) => setPropertySell(e.target.value)}
                    placeholder="e.g. 200"
                    size="small"
                    type="number"
                    value={propertySell}
                  />
                  <TextField
                    inputProps={{ min: 0 }}
                    label={`Net rate increase (${netCurrency})`}
                    onChange={(e) => setPropertyNet(e.target.value)}
                    placeholder="e.g. 150"
                    size="small"
                    type="number"
                    value={propertyNet}
                  />
                </Box>
              )}
            </Box>

            <Box bgcolor="action.hover" borderRadius={1} mt={2} p={1.5}>
              <Typography variant="body2">
                {pluralizeToString('experience', selectedExperiences.length)} · Experience sell +$
                {Math.round(experienceSellTotal)} · Experience cost ~${Math.round(experienceCostTotal)} AUD (not added
                to package net)
              </Typography>
              <Typography variant="body2">
                Estimated total sell uplift +${Math.round(experienceSellTotal + propertySellValue)} · Package net uplift
                +{netCurrency} {Math.round(propertyNetValue)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Typography color="text.secondary" sx={{ mr: 'auto' }} variant="body2">
          Step {step} of 3
        </Typography>
        {step > 1 && (
          <Button onClick={() => setStep(step - 1)} variant="text">
            Back
          </Button>
        )}
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button disabled={nextDisabled} onClick={handleNext} variant="contained">
          {step === 3 ? 'Create upgrade packages' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
